import { TableCell, TableRow, Typography, Box, useTheme } from "@mui/material";
import { getKeySuffix, slugify } from "@covertree/utils";
import { CustomAccordion, Tooltip } from "@covertree/ui";
import { PersonalizedPlanDetailsDescription } from "@covertree/new-business-forms";
import { Coverage } from "@covertree/new-business-mappings";

interface Props {
  customClass?: string;
  rowHeader?: Coverage;
  rowData?: PersonalizedPlanDetailsDescription[];
  componentData?: JSX.Element[];
}

export function CompareOfferTableRow({
  customClass,
  rowHeader,
  rowData,
  componentData,
}: Props) {
  const theme = useTheme();

  // do not render row when all plans has no value in coverage
  if (
    rowData &&
    rowData.every((data) => data && "value" in data && !data.value)
  ) {
    return <></>;
  }

  return (
    <TableRow
      sx={{
        verticalAlign: "top",
      }}>
      <TableCell>
        {rowHeader && (
          <CustomAccordion
            content={
              <Typography
                sx={(theme) => ({
                  color: theme.palette.info.main,
                  paddingRight: theme.spacing(5),
                })}
                variant="body2">
                {rowHeader.getDescription()}
              </Typography>
            }
            header={(isExpanded: boolean) => (
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                sx={{
                  paddingY: 0,
                  margin: 0,
                }}>
                <img
                  src={rowHeader.image}
                  alt={rowHeader.getName()}
                  style={{
                    maxWidth: theme.spacing(10),
                    height: theme.spacing(10),
                    objectFit: "contain",
                  }}
                />
                <Typography
                  sx={(theme) => ({
                    paddingLeft: theme.spacing(2),
                  })}
                  color={isExpanded ? "secondary" : "inherit"}>
                  {rowHeader.getName()}
                </Typography>
              </Box>
            )}
          />
        )}
      </TableCell>
      {rowData &&
        rowData.map((data, index) => (
          <TableCell
            key={getKeySuffix(data.name, index.toString())}
            sx={(theme) => ({
              verticalAlign: "middle",
              backgroundColor:
                index === 1 ? `${theme.palette.info.main}0D` : undefined,
            })}
            className={customClass}>
            {data && (
              <>
                <Box display="flex" flexDirection="row">
                  <Typography>{data.value}</Typography>
                  {data.tooltip && (
                    <Box
                      sx={(theme) => ({
                        marginLeft: theme.spacing(1),
                      })}>
                      <Tooltip
                        dataTestId={`${slugify(rowHeader!.getName())}_${index}`}
                        tooltip={data.tooltip}
                      />
                    </Box>
                  )}
                </Box>
                {data.description && (
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.info.main,
                      fontSize: "1rem",
                      marginTop: theme.spacing(1),
                    })}>
                    {data.description}
                  </Typography>
                )}
              </>
            )}
          </TableCell>
        ))}
      {componentData &&
        componentData.map((data, index) => (
          <TableCell
            key={data.key}
            sx={(theme) => ({
              verticalAlign: "middle",
              backgroundColor:
                index === 1 ? `${theme.palette.info.main}0D` : undefined,
            })}
            className={customClass}>
            {data}
          </TableCell>
        ))}
    </TableRow>
  );
}
